import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import { XCircle, AlertTriangle, TrendingDown, Users } from 'lucide-react';
import ReportLayout, { ReportTable, usePagination, fmtId } from './ReportLayout';
import { ReportChartRow, BarCard, groupCount, groupByMonth } from './ReportCharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useShiftTypes } from '../../hooks/useSettings';
import { fmtTime12 } from '../../utils/time';

const fmtDate = v => { try { return v ? format(parseISO(v), 'd MMM yyyy') : '–'; } catch { return v||'–'; } };

const COLS = [
  { key:'shift_id',       label:'Shift ID',       render: fmtId },
  { key:'staff_name',     label:'Staff Name',      render: v => v||<span className="text-slate-400 italic">Unassigned</span> },
  { key:'client',         label:'Client' },
  { key:'shift_date',     label:'Date',            render: fmtDate },
  { key:'shift_time',     label:'Shift Time',      render: v => v ? fmtTime12(v) : '–' },
  { key:'duration',       label:'Duration (hrs)' },
  { key:'shift_type',     label:'Shift Type' },
  { key:'pay_group',      label:'Pay Group' },
  { key:'declined_status',label:'Status',          render: v => <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 capitalize">{v||'–'}</span> },
  { key:'unfilled',       label:'Unfilled',        render: v => v ? <span className="text-red-500 font-semibold text-xs">Yes</span> : <span className="text-green-600 text-xs">No</span> },
];

export default function DeclinedShifts() {
  const [data, setData]     = useState([]);
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEnd]     = useState(format(new Date(), 'yyyy-MM-dd'));
  const [shiftType, setShiftType] = useState('');
  const shiftTypes = useShiftTypes();
  const pagination = usePagination(data);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (shiftType) p.set('shift_type', shiftType);
    Promise.all([
      api.get(`/reports/declined-shifts?${p}`),
      api.get(`/reports/declined-shifts/stats?${p}`),
    ])
      .then(([r, s]) => { setData(r.data||[]); setStats(s.data||null); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, shiftType]);

  useEffect(() => { load(); }, [load]);

  const chips = [
    { label:'Date Range', value:`${startDate} - ${endDate}` },
    ...(shiftType ? [{ label:'Shift Type', value: shiftType, onRemove: () => setShiftType('') }] : []),
  ];

  return (
    <ReportLayout title="Declined Shifts by Staff" data={data} filename="declined_shifts" columns={COLS}
      activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v=>!v)}
      chart={
        <ReportChartRow>
          <BarCard title="Most declines by staff" data={groupCount(data, 'staff_name', { limit: 8 })} color="#EF4444" horizontal />
          <BarCard title="Declines by month" data={groupByMonth(data, 'shift_date')} color="#F59E0B" />
        </ReportChartRow>
      }
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Shift Type</label>
            <select value={shiftType} onChange={e => setShiftType(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Types</option>
              {shiftTypes.filter(t => t.is_active).map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select></div>
          <div className="flex items-end"><button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button></div>
        </div>
      }>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
              <XCircle size={20} className="text-red-500"/>
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.total_declined||0}</p>
              <p className="text-xs text-slate-500 mt-0.5">Total Declined</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center">
              <AlertTriangle size={20} className="text-amber-500"/>
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.unfilled||0}</p>
              <p className="text-xs text-slate-500 mt-0.5">Still Unfilled</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
              <TrendingDown size={20} className="text-blue-500"/>
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.pct_unfilled||0}%</p>
              <p className="text-xs text-slate-500 mt-0.5">% Unfilled</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary banner — computed from query, not AI */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold shrink-0">i</div>
        <div>
          <p className="text-sm font-semibold text-blue-900">Summary</p>
          <p className="text-xs text-blue-700 mt-0.5">
            {data.length === 0
              ? 'No declined shifts in this date range.'
              : `${data.length} declined shift${data.length !== 1 ? 's' : ''} in the selected period. ${stats?.pct_unfilled > 50 ? 'High unfilled rate — review staffing capacity.' : 'Unfilled rate is within acceptable range.'}`
            }
          </p>
        </div>
      </div>

      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination}/>
    </ReportLayout>
  );
}
